import React, { useState } from "react";
import {
	CartesianGrid,
	Line,
	LineChart,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent } from "../../components/ui/card";
import {
	ChartConfig,
	ChartContainer,
	ChartTooltip,
} from "../../components/ui/chart";
import { P3Desk } from "../../styles/styles_text";
import Calendar from "../../assets/kinologyIcons/svg/calendar_gray.svg";
import SearchUsers from "./SearchUsers";

interface LabelsTypes {
	label: string;
	color: string;
}

interface EvolutionChartProps {
	title: string;
	subtitle?: string;
	labels: Array<LabelsTypes>;
	chartData: any;
	fills: {
		fill_baixo_risco: string;
		fill_medio_risco: string;
		fill_alto_risco_1: string;
		fill_alto_risco_2: string;
	};
}

const EvolutionChart: React.FC<EvolutionChartProps> = ({
	title,
	subtitle,
	labels,
	chartData,
	fills,
}) => {
	const chartConfig = {
		desktop: {
			label: "Desktop",
			color: "hsl(var(--chart-1))",
		},
		mobile: {
			label: "Mobile",
			color: "hsl(var(--chart-2))",
		},
	} satisfies ChartConfig;

	const [isTooltipVisible, setIsTooltipVisible] = useState(false);

	const [activeIndex, setActiveIndex]: any = useState(null);

	function _renderLabel(label: string, color: string) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					gap: "0.5rem",
					marginRight: "1rem",
				}}
			>
				<div
					style={{
						width: "1.2rem",
						height: "0.2rem",
						backgroundColor: color,
						borderRadius: "5px",
					}}
				></div>
				<P3Desk style={{ textAlign: "center" }}>{label}</P3Desk>
			</div>
		);
	}

	return (
		<Card
			style={{
				padding: "1rem",
				display: "flex",
				flexDirection: "column",
				backgroundColor: "#FFFFFF",
				width: "100%",
				marginBottom: "0.75rem",
				marginTop: "0.75rem",
				position: "relative",
			}}
		>
			<P3Desk style={{ textAlign: "center" }}>{title}</P3Desk>
			{subtitle && <P3Desk style={{ textAlign: "center" }}>{subtitle}</P3Desk>}
			<div
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "row",
					justifyContent: "center",
				}}
			>
				{labels.map((label) => _renderLabel(label.label, label.color))}
			</div>
			<div
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "row",
					justifyContent: "center",
					margin: "0.3rem",
					marginBottom: "3rem",
				}}
			>
				<img src={Calendar} alt="" style={{ marginRight: "0.5rem" }} />
				<P3Desk>2025</P3Desk>
			</div>
			<CardContent>
				<ChartContainer
					config={chartConfig}
					style={{ height: "300px", width: "100%" }}
					onMouseLeave={() => setIsTooltipVisible(false)}
				>
					<LineChart
						accessibilityLayer
						data={chartData}
						onClick={() => setIsTooltipVisible(true)}
						margin={{
							left: 12,
							right: 12,
						}}
					>
						<CartesianGrid
							vertical={true}
							onClick={() => setIsTooltipVisible(true)}
						/>
						<XAxis
							dataKey="month"
							tickLine={false}
							axisLine={false}
							tickMargin={8}
							tickFormatter={(value) => value.slice(0, 3)}
						/>
						<YAxis tickLine={false} tickMargin={10} axisLine={false} />

						<ChartTooltip
							cursor={{ fill: "rgba(0, 0, 0, 0)" }}
							active={isTooltipVisible}
							content={<SearchUsers />}
							trigger="click"
						/>
						<Line
							dataKey="alto_risco_1"
							type="linear"
							stroke={fills.fill_alto_risco_1}
							strokeWidth={activeIndex == "alto_risco_1" ? 5 : 2}
							dot={{
								fill: fills.fill_alto_risco_1,
							}}
							activeDot={{
								r: 6,
							}}
							onMouseEnter={() => setActiveIndex("alto_risco_1")}
							onMouseLeave={() => setActiveIndex(null)}
							onClick={() => setIsTooltipVisible(true)}
						/>
						<Line
							dataKey="alto_risco_2"
							type="linear"
							stroke={fills.fill_alto_risco_2}
							strokeWidth={activeIndex == "alto_risco_2" ? 5 : 2}
							dot={{
								fill: fills.fill_alto_risco_2,
							}}
							activeDot={{
								r: 6,
							}}
							onMouseEnter={() => setActiveIndex("alto_risco_2")}
							onMouseLeave={() => setActiveIndex(null)}
							onClick={() => setIsTooltipVisible(true)}
						/>
						<Line
							dataKey="medio_risco"
							type="linear"
							stroke={fills.fill_medio_risco}
							strokeWidth={activeIndex == "medio_risco" ? 5 : 2}
							dot={{
								fill: fills.fill_medio_risco,
							}}
							activeDot={{
								r: 6,
							}}
							onMouseEnter={() => setActiveIndex("medio_risco")}
							onMouseLeave={() => setActiveIndex(null)}
							onClick={() => setIsTooltipVisible(true)}
						/>
						<Line
							dataKey="baixo_risco"
							type="linear"
							stroke={fills.fill_baixo_risco}
							strokeWidth={activeIndex == "baixo_risco" ? 5 : 2}
							dot={{
								fill: fills.fill_baixo_risco,
							}}
							activeDot={{
								r: 6,
							}}
							onClick={() => setIsTooltipVisible(true)}
							onMouseEnter={() => setActiveIndex("baixo_risco")}
							onMouseLeave={() => setActiveIndex(null)}
						/>
					</LineChart>
				</ChartContainer>
			</CardContent>
		</Card>
	);
};

export default EvolutionChart;

